/*
  NUDIENT: Färgprickarna i kortrutnätet, paket E4, M-094 punkt 5 och M-098
  punkt 7. Plan: nudient/PLAN-E4-kortrutnat.md punkt 11. Laddas av
  sections/nudient-collection-grid.liquid.

  Sidan ritas utan prickar, med en reserverad rad per kort,
  [data-nudient-dots], så att korten inte hoppar. Skriptet hämtar sedan
  prickarna ur sections/nudient-collection-dots.liquid, med Shopifys Section
  Rendering API och sidans filter och sortering, och sätter in dem.

  - Sektionen delar kollektionen i sidor om 36. Skriptet räknar ut vilka av
    dem som täcker rutnätets kort, ur rutnätets sida och antal per sida, och
    hämtar en eller två.
  - Svaret sparas i sessionStorage per adress, så att bakåt och framåt inte
    frågar igen.
  - När filtret eller sorteringen ritar om rutnätet fylls de nya raderna, med
    en MutationObserver på sektionen.
  - Svarar sektionen inte, eller fel, står raderna tomma. Inget annat
    påverkas.

  Värden som inte är tokens:
  - 36: sektionens sidstorlek, M-098 punkt 5.
  - 3000: väntetiden i millisekunder, som assets/nudient-bundle.js.
  - nudient-collection-dots och nudient-dots:1: sektionen, svarets id och
    början på nyckeln i sessionStorage.
  - section_id och page: Shopifys parametrar.
  - nudient-dots-filled: en tidsmarkering i webbläsaren när raderna är
    fyllda, så att tiden kan mätas, planen punkt 11.
*/

const CHUNK = 36;
const TIMEOUT = 3000;

/** @param {number} chunk */
async function fetchChunk(chunk) {
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', 'nudient-collection-dots');
  url.searchParams.set('page', String(chunk));
  url.hash = '';
  const key = `nudient-dots:1:${url.pathname}${url.search}`;
  try {
    const saved = sessionStorage.getItem(key);
    if (saved) return JSON.parse(saved);
  } catch {}

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT);
  const response = await fetch(url, { signal: controller.signal });
  clearTimeout(timer);
  if (!response.ok) return {};
  const script = new DOMParser().parseFromString(await response.text(), 'text/html').getElementById('nudient-collection-dots');
  const dots = JSON.parse(script?.textContent || '{}');
  try {
    sessionStorage.setItem(key, JSON.stringify(dots));
  } catch {}
  return dots;
}

class NudientCollectionDots extends HTMLElement {
  /** @type {MutationObserver | undefined} */ #observer;
  #busy = false;

  connectedCallback() {
    this.#fill();
    const section = this.closest('.shopify-section');
    if (!section) return;
    this.#observer = new MutationObserver(() => {
      if (section.querySelector('[data-nudient-dots]:not([data-filled])')) this.#fill();
    });
    this.#observer.observe(section, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #fill() {
    if (this.#busy) return;
    this.#busy = true;
    try {
      const section = this.closest('.shopify-section') || document;
      const holder = section.querySelector('nudient-collection-dots') || this;
      const page = Number(holder.dataset.page) || 1;
      const perPage = Number(holder.dataset.perPage) || CHUNK;
      const first = (page - 1) * perPage;
      const last = first + perPage - 1;
      const chunks = [];
      for (let chunk = Math.floor(first / CHUNK) + 1; chunk <= Math.floor(last / CHUNK) + 1; chunk++) chunks.push(chunk);

      let dots = {};
      for (const part of await Promise.all(chunks.map((chunk) => fetchChunk(chunk).catch(() => ({}))))) {
        dots = { ...dots, ...part };
      }
      for (const row of section.querySelectorAll('[data-nudient-dots]:not([data-filled])')) {
        const html = dots[/** @type {HTMLElement} */ (row).dataset.nudientDots || ''];
        if (html) row.innerHTML = html;
        row.setAttribute('data-filled', '');
      }
      performance.mark('nudient-dots-filled');
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('nudient-collection-dots')) {
  customElements.define('nudient-collection-dots', NudientCollectionDots);
}
